import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
	MdDownload,
	MdCheck,
	MdDarkMode,
	MdNotifications,
	MdVolumeUp,
	MdLogout,
	MdArrowBack,
	MdLanguage,
	MdPerson,
	MdPrivacyTip,
	MdHelp,
	MdInfo,
	MdSecurity,
	MdStorage,
	MdBrush,
} from "react-icons/md";
import { useAuth } from "../hooks/useAuth";
import { useStorage } from "../hooks/useStorage";
import { useServer } from "../hooks/useServer";

import { generateUUID } from "../utils/Helpers";
import { useToast } from "../hooks/ToastProvider";

const Settings = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { showToast } = useToast();
	const { user, getPronunciationAssessmentContent, downloadBuild } =
		useServer();
	const { useLocalStorage, saveToStorage, setLocalStorage } = useStorage();
	const { logout, isAuthenticated, claims } = useAuth();

	const [darkMode, setDarkMode] = useLocalStorage("darkMode", false);
	const [notificationsEnabled, setNotificationsEnabled] = useLocalStorage(
		"notifications",
		true
	);
	const [soundEnabled, setSoundEnabled] = useLocalStorage("sound", true);

	const toggleDarkMode = () => {
		setDarkMode(!darkMode);
		showToast("Theme changed");
	};

	const toggleNotifications = () => {
		setNotificationsEnabled(!notificationsEnabled);
		showToast("Notification settings updated");
	};

	const toggleSound = () => {
		setSoundEnabled(!soundEnabled);
		showToast("Sound settings updated");
	};

	const handleDownloadApp = async (platform: string) => {
		const url = await downloadBuild(platform);
		window.open(url, "_blank");
	};

	const SettingItem = ({
		icon: Icon,
		title,
		onClick,
		toggle,
		enabled,
	}: any) => (
		<div className="flex items-center justify-between p-3 rounded-xl hover:bg-indigo-50 transition-colors">
			<div className="flex items-center gap-3">
				<Icon className="text-xl text-indigo-600" />
				<span className="font-medium text-gray-700">{title}</span>
			</div>
			{toggle ? (
				<button
					onClick={onClick}
					className={`w-12 h-6 rounded-full ${
						enabled ? "bg-indigo-600" : "bg-gray-300"
					} relative transition-colors`}
				>
					<span
						className={`absolute top-1 ${
							enabled ? "right-1" : "left-1"
						} w-4 h-4 bg-white rounded-full transition-all`}
					/>
				</button>
			) : (
				<MdArrowBack className="text-xl text-gray-400 rotate-180" />
			)}
		</div>
	);

	return (
		<div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
			<div className="px-4 py-6">
				{/* Header with Back Button */}
				<header className="flex items-center gap-4 mb-6">
					<button
						onClick={() => navigate(-1)}
						className="p-2 rounded-full hover:bg-indigo-100 transition-colors"
					>
						<MdArrowBack className="text-2xl text-indigo-600" />
					</button>
					<h1 className="text-2xl font-bold text-gray-900">{t("settings")}</h1>
				</header>

				<div className="space-y-6">
					{/* Account Section */}
					<div className="bg-white rounded-2xl shadow-sm p-4">
						<h2 className="text-lg font-semibold text-indigo-900 mb-4">
							{t("account")}
						</h2>
						<div className="space-y-2">
							<SettingItem
								icon={MdPerson}
								title={t("profile")}
								onClick={() => navigate("/profile")}
							/>
							<SettingItem
								icon={MdSecurity}
								title={t("security")}
								onClick={() => navigate("/security")}
							/>
						</div>
					</div>

					{/* Preferences Section */}
					<div className="bg-white rounded-2xl shadow-sm p-4">
						<h2 className="text-lg font-semibold text-indigo-900 mb-4">
							{t("preferences")}
						</h2>
						<div className="space-y-2">
							<SettingItem
								icon={MdDarkMode}
								title={t("darkMode")}
								toggle
								enabled={darkMode}
								onClick={toggleDarkMode}
							/>
							<SettingItem
								icon={MdNotifications}
								title={t("notifications")}
								toggle
								enabled={notificationsEnabled}
								onClick={toggleNotifications}
							/>
							<SettingItem
								icon={MdVolumeUp}
								title={t("sound")}
								toggle
								enabled={soundEnabled}
								onClick={toggleSound}
							/>
							<SettingItem
								icon={MdLanguage}
								title={t("language")}
								onClick={() => navigate("/language")}
							/>
							<SettingItem
								icon={MdBrush}
								title={t("appearance")}
								onClick={() => navigate("/appearance")}
							/>
						</div>
					</div>

					{/* Storage and Data */}
					<div className="bg-white rounded-2xl shadow-sm p-4">
						<h2 className="text-lg font-semibold text-indigo-900 mb-4">
							{t("storageAndData")}
						</h2>
						<div className="space-y-2">
							<SettingItem
								icon={MdStorage}
								title={t("dataUsage")}
								onClick={() => navigate("/data-usage")}
							/>
						</div>
					</div>

					{/* Help and Support */}
					<div className="bg-white rounded-2xl shadow-sm p-4">
						<h2 className="text-lg font-semibold text-indigo-900 mb-4">
							{t("helpAndSupport")}
						</h2>
						<div className="space-y-2">
							<SettingItem
								icon={MdHelp}
								title={t("help")}
								onClick={() => navigate("/help")}
							/>
							<SettingItem
								icon={MdPrivacyTip}
								title={t("privacyPolicy")}
								onClick={() => navigate("/privacy")}
							/>
							<SettingItem
								icon={MdInfo}
								title={t("about")}
								onClick={() => navigate("/about")}
							/>
						</div>
					</div>

					{/* Admin Section */}
					{claims?.admin && (
						<div className="bg-white rounded-2xl shadow-sm p-4">
							<h2 className="text-lg font-semibold text-indigo-900 mb-4">
								{t("admin")}
							</h2>
							<div className="space-y-4">
								<button
									onClick={() => {
										setLocalStorage("completedOnboarding", false);
										navigate("/liveTutor");
									}}
									className="w-full p-3 bg-indigo-50 text-gray-700 rounded-xl hover:bg-indigo-100 transition-colors flex items-center justify-between"
								>
									<span className="font-medium">Navigate to Onboarding</span>
									<MdCheck className="text-xl text-indigo-600" />
								</button>
							</div>
						</div>
					)}

					{/* Logout Button */}
					{isAuthenticated() && (
						<button
							onClick={logout}
							className="w-full py-4 mt-4 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors flex items-center justify-center gap-2 font-medium"
						>
							<MdLogout className="text-xl" />
							{t("logout")}
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

export default Settings;
